/**
 * How sharp the 3D view is drawn: pixels per screen point (WebGL's pixel ratio). Desktop screens and
 * phones alike start at 2 (sharp on any screen: a phone's 3 is hard to tell from it), or the screen's own
 * when that's less, and step down (1.75, 1.5, 1.25) when the device can't keep the game smooth. Below the
 * screen's own too (1, 0.85, 0.75: a touch softer, the browser scales it up): a PC screen at normal
 * scaling starts at 1, and without those it had nowhere to go.
 *
 * It never changes during a lap. Frame times are watched on the poster (the 3D scene draws behind it the
 * whole time) and while racing; a step down happens only at a safe moment (the poster, the pause menu),
 * so a race is never interrupted by a redraw. A device keeps its level for a week, then tries the sharper
 * one again (on the poster, where a step back down goes unnoticed).
 *
 * One slow stretch isn't the device's pace (another program busy for a moment): it takes two in a row. And a
 * big step down skips levels that were never tried: only the level it was at was seen to drop frames. So the
 * next visit starts just below that one, once a week (a Mac drawing 2 in Discord had sat a week at 0.75 in
 * its browser after one bad moment, "the game looks quite blurry").
 */
export const LEVELS = [2, 1.75, 1.5, 1.25, 1, 0.85, 0.75];

/**
 * What a browser drawing without the graphics card calls its renderer (Chrome's SwiftShader, Mesa's llvmpipe,
 * Windows' Basic Render Driver): then no sharpness makes the game smooth (render/world.ts drawsInSoftware).
 */
export const SOFTWARE_GL = /swiftshader|llvmpipe|softpipe|basic render driver|software/i;

/** Where the game is: the poster or pause menu (a change is safe), racing or watching (never), or nothing to judge. */
export type Moment = 'safe' | 'racing' | 'idle';

/** A frame slower than this (ms) is a dropped frame at 60 Hz. */
const SLOW_FRAME = 18.5;
/** Frames are judged a second and a half at a time (however few a slow device manages in that time). */
const WINDOW_MS = 1500;
const WINDOW_MIN = 8;
const STORE_KEY = 'gp.sharp';
const KEEP_DAYS = 7;
/** Slow stretches in a row before a step down. */
const SLOW_RUNS = 2;

export class Sharpness {
  /** The levels this screen can use, sharpest first (a screen of 1.5 pixels a point gets 1.5 and 1.25). */
  readonly levels: number[];
  private i = 0;
  private frames: number[] = [];
  private framesMs = 0;
  private quietUntil = 0;
  private lastMoment: Moment = 'idle';
  /** Racing showed the device can't keep up: step down at the next safe moment. */
  private pendingDown = false;
  /** Slow stretches in a row so far. */
  private slowRuns = 0;
  /** The least sharp level seen to drop frames (an index; -1: none). */
  private bad = -1;
  /** This visit started sharper than where the device settled, trying the levels a step down skipped. */
  private probing = false;
  /** Such a try dropped frames too: the device keeps where it settles for the rest of the week. */
  private probed = false;
  /** Even the lowest level drops frames. */
  struggling = false;
  /** A level picked by hand (?sharp=1.5): no adapting. */
  readonly fixed: boolean;
  /** The level changed (the renderer must be resized). */
  onChange: (ratio: number, why: string) => void = () => {};

  constructor(dpr: number, forced: number | null = null, now = Date.now()) {
    const top = Math.min(dpr || 1, LEVELS[0]);
    this.levels = [top, ...LEVELS.filter((l) => l < top - 0.01)];
    this.fixed = forced !== null;
    if (forced !== null) {
      this.levels = [forced];
      return;
    }
    // Where this device settled last time (for a week).
    try {
      const s = JSON.parse(localStorage.getItem(STORE_KEY) ?? 'null') as { ratio?: number; at?: number; bad?: number; probed?: boolean } | null;
      if (s && typeof s.ratio === 'number' && typeof s.at === 'number' && now - s.at < KEEP_DAYS * 86400000) {
        const at = (r: unknown): number => (typeof r === 'number' ? this.levels.findIndex((l) => Math.abs(l - r) < 0.01) : -1);
        const k = at(s.ratio);
        this.bad = at(s.bad);
        this.probed = s.probed === true;
        // Just below the least sharp level seen to fail (a level kept from before this was kept: the top).
        if (k > 0) this.i = this.probed ? k : Math.min(k, this.bad + 1);
        this.probing = this.i < k;
      }
    } catch {
      // No storage: start sharp every visit.
    }
  }

  get ratio(): number {
    return this.levels[this.i];
  }

  get atLowest(): boolean {
    return this.i === this.levels.length - 1;
  }

  /** The lowest level struggled, and the game drew less some other way (the light scene): judge it afresh. */
  retry(now: number): void {
    this.struggling = false;
    this.pendingDown = false;
    this.quiet(now);
  }

  /** Don't judge for a moment: just loaded, back from another tab, or just changed (shaders warming up). */
  quiet(now: number, ms = 1500): void {
    this.quietUntil = Math.max(this.quietUntil, now + ms);
    this.frames.length = 0;
    this.framesMs = 0;
    this.slowRuns = 0;
  }

  /** One frame: how long it took (ms) and where the game was. */
  frame(dtMs: number, moment: Moment, now: number): void {
    if (this.fixed) return;
    if (moment !== this.lastMoment) {
      // The poster and racing draw different scenes: each is judged on its own frames. Just loaded:
      // the first frames compile shaders, so wait longer.
      this.quiet(now, this.lastMoment === 'idle' ? 1500 : 400);
      this.lastMoment = moment;
    }
    if (moment === 'safe' && this.pendingDown) {
      this.pendingDown = false;
      this.stepDown(now, 'racing dropped frames', this.pendingTo);
      return;
    }
    // A frame of a second or more is a tab switch or a stall, not the device's pace.
    if (moment === 'idle' || now < this.quietUntil || dtMs <= 0 || dtMs > 1000) return;
    this.frames.push(dtMs);
    this.framesMs += dtMs;
    if (this.framesMs < WINDOW_MS || this.frames.length < WINDOW_MIN) return;
    const verdict = judge(this.frames);
    const median = this.frames.slice().sort((a, b) => a - b)[this.frames.length >> 1];
    this.frames.length = 0;
    this.framesMs = 0;
    if (verdict !== 'slow') {
      this.slowRuns = 0;
      return;
    }
    if (++this.slowRuns < SLOW_RUNS) return;
    this.slowRuns = 0;
    if (this.atLowest) {
      this.struggling = true;
      return;
    }
    // About how many fewer pixels would do: the frame time scales with them.
    const to = this.levelFor(median);
    if (moment === 'safe') this.stepDown(now, 'the poster dropped frames', to);
    else {
      this.pendingDown = true;
      this.pendingTo = Math.max(this.pendingTo, to);
    }
  }

  private pendingTo = 0;

  /** The level (index) that should bring frames under budget: the pixels scale with the level squared. */
  private levelFor(medianMs: number): number {
    const want = this.ratio * Math.sqrt(SLOW_FRAME / medianMs);
    let k = this.i + 1;
    while (k < this.levels.length - 1 && this.levels[k] > want) k++;
    return Math.min(k, this.levels.length - 1);
  }

  private stepDown(now: number, why: string, to = this.i + 1): void {
    if (this.atLowest) return;
    if (this.i > this.bad) this.bad = this.i;
    if (this.probing) this.probed = true;
    this.i = Math.min(this.levels.length - 1, Math.max(this.i + 1, to));
    this.pendingTo = 0;
    this.quiet(now);
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ ratio: this.ratio, at: Date.now(), bad: this.levels[this.bad], probed: this.probed }));
    } catch {
      // Not remembered: it settles again next visit.
    }
    this.onChange(this.ratio, why);
  }
}

/**
 * A second and a half of frames: smooth, or slow (most of them dropped, or a steady trickle of long ones).
 * A device held at 30 frames a second by the system (an iPhone in Low Power Mode) isn't slow: its frames
 * sit evenly at 33 ms, and drawing fewer pixels wouldn't bring 60 back.
 */
export function judge(frames: number[]): 'smooth' | 'slow' | 'capped' {
  const s = frames.slice().sort((a, b) => a - b);
  const at = (q: number): number => s[Math.min(s.length - 1, Math.floor(q * s.length))];
  const median = at(0.5);
  if (median > 31.5 && median < 35.5 && at(0.9) - at(0.1) < 4) return 'capped';
  if (median > SLOW_FRAME) return 'slow';
  // Mostly smooth but one frame in five dropped: still visibly juddery.
  if (at(0.8) > SLOW_FRAME * 1.3) return 'slow';
  return 'smooth';
}
